import { ErrorType } from "../../../models/HttpError";
import { AppState } from "../../../redux/reducers";

// store 
import * as ContractConceptRelationshipStore from '../../../redux/middleware/ContractConceptRelationship.Middleware';

// models
import { ContractConceptRelationship } from "../../../models/Entities/Contracts/ContractConceptRelationship";

interface IProps {
  ContractId: number;
  formId?: number;
  formName?: string;
  label?: string;
  itemNumber?: number;
  value?: string | number;
  realized?: boolean;
  triggerAs?: React.ReactNode;
}

interface ConnectedProps {
  // loading
  isLoading: boolean;
  isLoadingSuccessfully: boolean;
  failOnLoading: boolean;

  // updatimg
  isUpdating: boolean;
  isUpdatedSuccessfully: boolean;
  failOnUpdating: boolean;

  isDeleting: boolean;
  successDeletingOne: boolean | undefined;
  failOnDeleting: boolean;
  
  // create
  isCreating: boolean;
  isCreatedSuccessfully: boolean;
  failOnCreating: boolean;

  error: ErrorType | undefined;
  ConceptRelationships: ContractConceptRelationship[];
  isAdding: boolean;
  successAdding: boolean | undefined;
  CurrentConceptRelationship: ContractConceptRelationship[];
}

interface MedicalServicesDispatchProps {
 getAllContractConceptRelationships: typeof ContractConceptRelationshipStore.actionCreators.getAllContractConceptRelationships;
 createContractConceptRelationship: typeof ContractConceptRelationshipStore.actionCreators.createContractConceptRelationship;
 updateContractConceptRelationship: typeof ContractConceptRelationshipStore.actionCreators.updateContractConceptRelationship;
 deleteContractConceptRelationship: typeof ContractConceptRelationshipStore.actionCreators.deleteContractConceptRelationship;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

/**
 * Empareja una relacion con el formulario en contexto.
 *
 * Se prioriza formId, que es univoco: dos formularios pueden compartir titulo y
 * por nombre se confunden. Los registros creados antes de que el alta enviara
 * formId no lo tienen; para esos se cae al nombre, porque si no dejarian de
 * encontrarse y cada guardado generaria un duplicado en vez de actualizarlos.
 */
export const matchesFormContext = (
  relationship: Pick<ContractConceptRelationship, "formId" | "formName">,
  formId: number | undefined,
  formName: string | undefined
): boolean => {
  if (formId !== undefined && formId !== null && relationship.formId) {
    return relationship.formId === formId;
  }

  return relationship.formName?.toLowerCase() === formName?.toLowerCase();
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => ({
    // loading
    isLoading: state.ContractConceptRelationship?.isLoadingAll || false,
    isLoadingSuccessfully: state.ContractConceptRelationship?.successLoadingAll || false,
    failOnLoading: state.ContractConceptRelationship?.failOnLoadingAll || false,

    // updating
    isUpdating: state.ContractConceptRelationship?.isUpdatingOne || false,
    isUpdatedSuccessfully: state.ContractConceptRelationship?.successUpdatingOne || false,
    failOnUpdating: state.ContractConceptRelationship?.failOnUpdatingOne || false,

    isDeleting: state.ContractConceptRelationship?.isDeletingOne || false,
    successDeletingOne: state.ContractConceptRelationship?.successDeletingOne,
    failOnDeleting: state.ContractConceptRelationship?.failOnDeletingOne || false,

    // creating
    isCreating: state.ContractConceptRelationship?.isAddingNewOne || false,
    isCreatedSuccessfully: state.ContractConceptRelationship?.successAddingNewOne || false,
    failOnCreating: state.ContractConceptRelationship?.failOnAddingNewOne || false,
    
    error: state.ContractConceptRelationship?.error,
    ConceptRelationships: state.ContractConceptRelationship?.list || [],
    isAdding: state.ContractConceptRelationship?.isAddingNewOne || false,
    successAdding: state.ContractConceptRelationship?.successAddingNewOne,
    CurrentConceptRelationship: ownProps.label
      ? (() => {
          const labelUpper = (ownProps.label ?? '').toUpperCase();
          const isRealized = labelUpper === 'REALIZED' || !ownProps.label?.trim();
          const hasFormContext = ownProps.formId !== undefined || ownProps.formName !== undefined;
          return (state.ContractConceptRelationship?.list ?? []).filter((rel) => {
            if (rel.formConcept?.toUpperCase() !== labelUpper) return false;
            if (isRealized && hasFormContext && ownProps.itemNumber !== undefined) {
              return (
                matchesFormContext(rel, ownProps.formId, ownProps.formName) &&
                rel.itemNumber === ownProps.itemNumber
              );
            }
            return true;
          });
        })()
      : [],
});

export const mapDispatchToProps = {
  ...ContractConceptRelationshipStore.actionCreators,
};